import { useEffect } from 'react';
import { Box, styled } from '@mui/material';
import type { Control, FieldValues } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';

import PhoneInput from '@/components/forms/PhoneInput';
import { emptyStringIfOnlyNanpCallingCode } from '@/services/phoneUtils';

import type { ReservationFormInput } from '../schema/reservationForm.schema';
import { useReservationFormMachines } from '../ReservationFormMachinesContext';
import { useFieldActorSnapshot } from '../useFieldActorSnapshot';
import type { ReservationFormMainContext } from '../machines/reservationFormMain.machine';

const PREFIX = 'TripArrayFieldPhoneInput';

const classes = {
  container: `${PREFIX}-container`,
  extension: `${PREFIX}-extension`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    [theme.breakpoints.down('md')]: {
      width: '100%',
      '& .MuiFormControl-root': {
        width: '100%',
      },
    },
    [theme.breakpoints.up('md')]: {
      width: '50%',
    },
  },
  [`& .${classes.extension}`]: {
    [theme.breakpoints.down('md')]: {
      width: `calc(100% - ${theme.spacing(4)})`,
    },
  },
}));

export function TripArrayFieldPhoneInput({
  index,
  tripField,
  extensionField,
  label,
  required,
}: {
  index: number;
  tripField: 'pickupPhone' | 'dropoffPhone';
  extensionField: 'pickupPhoneExtension' | 'dropoffPhoneExtension';
  label: string;
  required?: boolean;
}) {
  const { control, formState, setValue } = useFormContext<ReservationFormInput>();
  const { fieldRefs } = useReservationFormMachines();
  const actor = fieldRefs[tripField];
  useFieldActorSnapshot(actor);

  const phoneName = `trips.${index}.${tripField}` as const;

  return (
    <StyledBox className={classes.container}>
      <PhoneInput
        label={label}
        name={phoneName}
        extensionName={`trips.${index}.${extensionField}`}
        control={control as unknown as Control<FieldValues>}
        errors={formState.errors}
        required={required}
        disableDropdown={true}
        onValueChange={(value) => {
          const normalized = emptyStringIfOnlyNanpCallingCode(value);
          actor.send({
            type: 'CHANGE',
            index,
            value: normalized,
          });
          setValue(phoneName, normalized, {
            shouldDirty: true,
            shouldValidate: formState.isSubmitted,
          });
        }}
      />
    </StyledBox>
  );
}
